'use client'

import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { ResponsiveLine } from '@nivo/line'
import { formatCurrencyWhole, formatCurrencyRounded } from '@repo/utils/numbers'
import MUITypography from '@mui/material/Typography'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import { ReportsIcon } from '@repo/ui/ReportsIcon'
import { EmptyState } from '@repo/ui/EmptyState'

// make sure parent container have a defined height when using
// responsive component, otherwise height will be 0 and
// no chart will be rendered.

// see documentation here: https://nivo.rocks/line

const renderTooltip = ({ slice, units }) => {
  return (
    <MUIPaper sx={{ padding: '12px', backgroundColor: 'white' }} elevation={3}>
      <MUITypography
        sx={{ width: '100%', borderBottom: '1px solid rgba(5, 28, 51, 0.1)', paddingBottom: '5px', mb: 3 }}
        variant="h5"
      >
        {slice.points[0].data.x}
      </MUITypography>

      {slice.points.map((series) => {
        return (
          <MUIStack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            key={series.id}
            sx={{ minWidth: '140px', mb: '10px' }}
          >
            <div
              style={{
                width: '14px',
                minWidth: '14px',
                height: '14px',
                borderRadius: '7px',
                backgroundColor: series.serieColor,
                marginRight: '8px'
              }}
            />
            <MUITypography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
              {`${series.serieId}`}
            </MUITypography>

            <MUITypography variant="body1" sx={{ mb: 0, textAlign: 'right', width: '100%' }}>
              {formatCurrencyWhole(series.data.y, units)}
            </MUITypography>
          </MUIStack>
        )
      })}
    </MUIPaper>
  )
}

export const DashboardChartBase = ({ data, target = 0, units = '' }) => {
  const [lowestValue, setLowestValue] = useState(0)
  const [highestValue, setHighestValue] = useState(target || 0)

  useEffect(() => {
    let lowestValue = 0
    let highestValue = target || 0

    data?.forEach((series) => {
      series.data.forEach((point) => {
        if (point.y < lowestValue) {
          lowestValue = point.y
        }

        if (point.y > highestValue) {
          highestValue = point.y
        }
      })
    })

    setLowestValue(lowestValue)
    setHighestValue(highestValue)
  }, [data, lowestValue, highestValue, setLowestValue, setHighestValue, target])

  const markers = target
    ? [
        {
          axis: 'y',
          legend: 'Target',
          legendPosition: 'right',
          legendOffsetX: 0,
          legendOffsetY: 0,
          legendOrientation: 'horizontal',
          lineStyle: {
            stroke: 'rgba(0, 0, 0, 0.6)',
            strokeWidth: 1,
            strokeDasharray: '24 6'
          },
          textStyle: {
            fontSize: '12px',
            fontFamily: 'Mulish',
            padding: 0
          },
          value: target
        }
      ]
    : null

  if (!data || data.length === 0) {
    return (
      <EmptyState
        text="No KPI data to show"
        icon={<ReportsIcon sx={{ fontSize: 56, mb: 4 }} />}
        sx={{ height: 'calc(100% - 48px)' }}
      />
    )
  }

  return (
    <div data-testid="DashboardChartBase" style={{ padding: '10px 40px 40px', width: '100%', height: '540px' }}>
      <ResponsiveLine
        data={data}
        theme={{
          text: {
            fontFamily: 'Mulish',
            fontSize: '12px'
          },
          axis: {
            legend: {
              text: {
                fontWeight: 600,
                fontSize: '14px'
              }
            }
          },
          grid: {
            line: {
              stroke: 'rgba(0,0,0, 0.1)'
            }
          },
          legends: {
            text: {
              fontWeight: 600,
              fontSize: '14px'
            }
          },
          tooltip: {
            container: {
              top: '50px'
            }
          }
        }}
        colors={{ datum: 'color' }}
        margin={{ top: 60, right: 50, bottom: 30, left: 50 }}
        xScale={{ type: 'point' }}
        yScale={{
          type: 'linear',
          min: lowestValue,
          max: highestValue,
          stacked: false,
          reverse: false
        }}
        gridYValues={4}
        yFormat=" >-0,.0~f"
        curve="monotoneX"
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: '', //  if set this, you might need to increase margin bottom above
          legendOffset: 50,
          legendPosition: 'middle',
          truncateTickAt: 0,
          format: (value) => value.slice(0, 3)
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 10,
          tickRotation: 0,
          tickValues: 5,
          legend: '',
          legendOffset: 0,
          legendPosition: 'middle',
          truncateTickAt: 0,
          format: (value) => formatCurrencyRounded(value, units)
        }}
        enableGridX={false}
        lineWidth={3}
        enablePoints={false}
        pointSize={8}
        pointColor={{ from: 'color' }}
        pointBorderWidth={2}
        pointBorderColor="white"
        pointLabel="data.yFormatted"
        pointLabelYOffset={-15}
        enableSlices="x"
        sliceTooltip={({ slice }) => renderTooltip({ slice, units })}
        enableCrosshair={true}
        enableTouchCrosshair={false}
        crosshairType="bottom"
        useMesh={true}
        markers={markers}
        legends={[
          {
            anchor: 'top',
            direction: 'row',
            justify: false,
            translateX: 0,
            translateY: -50,
            itemsSpacing: 0,
            itemDirection: 'left-to-right',
            itemWidth: 80,
            itemHeight: 20,
            itemOpacity: 1,
            symbolSize: 14,
            symbolShape: 'circle'
          }
        ]}
      />
    </div>
  )
}

DashboardChartBase.propTypes = {
  data: PropTypes.array,
  target: PropTypes.number,
  units: PropTypes.string
}
